import type { Car } from '@multirx/shared';
import { fetchCars, fetchCarById, importCarsBackup, deleteCar } from './api.js';
import {
  renderCarList,
  setCarListLoading,
  setCarListError,
  mountCarListToolbar,
  applyCarListFilters,
  updateCarListCount,
  syncCarListSelection,
  DEFAULT_CAR_LIST_FILTERS,
  type CarListFilters,
} from './carListView.js';
import { renderCarDetail, setCarDetailLoading, setCarDetailError } from './carDetailView.js';
import { mountCreateCarModal } from './createCarModal.js';
import { createGarageEntityTitleBlock } from '../garage/garageEntityHeader.js';
import { createPillButton } from '../ui/pillButton.js';
import { wrapInPillButtonGroup } from '../ui/pillButtonGroup.js';
import { openConfirmModal } from '../ui/confirmModal.js';
import { exportCarsToFile, readCarBackupFromFile } from '../garage/garageBackup.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import type { GaragePaneHandle, GaragePaneHosts } from '../garage/garageEmbed.js';
import { parseGarageNumericId } from '../garage/garageHash.js';

const SELECTED_ID_KEY = 'multirx-selected-car-id';
const FILTERS_KEY = 'multirx-car-list-filters';

function loadSavedFilters(): CarListFilters {
  try {
    const raw = sessionStorage.getItem(FILTERS_KEY);
    if (!raw) return { ...DEFAULT_CAR_LIST_FILTERS };
    const parsed = JSON.parse(raw) as CarListFilters;
    return { ...DEFAULT_CAR_LIST_FILTERS, ...parsed };
  } catch {
    return { ...DEFAULT_CAR_LIST_FILTERS };
  }
}

function saveFilters(filters: CarListFilters): void {
  sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
}

function appendCarActions(
  actionsHost: HTMLElement,
  opts: {
    onNew: () => void;
    onImportClick: () => void;
    onExport: () => void;
  }
): void {
  const newCarBtn = createPillButton('Nouveau', {
    variant: 'soft',
    ariaLabel: 'Nouvelle voiture',
    onClick: opts.onNew,
  });
  newCarBtn.dataset.role = 'admin';

  const importBtn = createPillButton('Importer', {
    variant: 'soft',
    onClick: opts.onImportClick,
  });
  importBtn.dataset.role = 'admin';

  const exportBtn = createPillButton('Exporter', {
    variant: 'soft',
    onClick: opts.onExport,
  });

  actionsHost.appendChild(
    wrapInPillButtonGroup([newCarBtn, importBtn, exportBtn], {
      small: true,
      overflow: true,
      ariaLabel: 'Actions voitures',
    })
  );
  applyRoleVisibility();
}

/** Montage embarqué dans le shell garage (liste + détail + actions). */
export function mountCarsPane(hosts: GaragePaneHosts): GaragePaneHandle {
  const { listHost, detailHost, actionsHost, modalRoot, initialId, onSelectId } = hosts;

  listHost.innerHTML = '';
  detailHost.innerHTML = '';
  actionsHost.innerHTML = '';

  const listContainer = document.createElement('div');
  listContainer.className = 'circuits-sidebar garage-sidebar';

  const listToolbar = document.createElement('div');
  listToolbar.className = 'garage-sidebar-toolbar-host';

  const listInner = document.createElement('div');
  listInner.className = 'circuits-sidebar-inner garage-sidebar-list';

  listContainer.append(listToolbar, listInner);
  listHost.appendChild(listContainer);

  const detailContainer = detailHost;
  detailContainer.classList.add('circuits-main');

  const importInput = document.createElement('input');
  importInput.type = 'file';
  importInput.accept = 'application/json,.json';
  importInput.hidden = true;
  modalRoot.appendChild(importInput);

  let cars: Car[] = [];
  let filters: CarListFilters = loadSavedFilters();
  const fromStore = parseInt(sessionStorage.getItem(SELECTED_ID_KEY) ?? '', 10);
  let selectedId: number | null =
    parseGarageNumericId(initialId ?? null) ??
    (Number.isFinite(fromStore) && fromStore > 0 ? fromStore : null);

  let openCreateCar: () => void = () => undefined;

  appendCarActions(actionsHost, {
    onNew: () => openCreateCar(),
    onImportClick: () => importInput.click(),
    onExport: () => {
      void (async () => {
        try {
          const all = await fetchCars(1000);
          if (all.length === 0) {
            window.alert('Aucune voiture à exporter.');
            return;
          }
          exportCarsToFile(all);
        } catch (err) {
          window.alert(err instanceof Error ? err.message : 'Export impossible.');
        }
      })();
    },
  });

  const toolbar = mountCarListToolbar(listToolbar, filters, (next) => {
    filters = next;
    saveFilters(filters);
    refreshList();
  });

  function refreshList(): void {
    const filtered = applyCarListFilters(cars, filters);
    updateCarListCount(listToolbar, filtered.length, cars.length);
    renderCarList(listInner, filtered, (id) => selectCar(id), selectedId, {
      emptyFiltered: cars.length > 0,
    });
  }

  const createModal = mountCreateCarModal(modalRoot, (car) => {
    cars = [car, ...cars];
    refreshList();
    if (car.id != null) selectCar(car.id);
  });
  openCreateCar = (): void => createModal.open();

  function loadList(): Promise<void> {
    setCarListLoading(listInner, true);
    return fetchCars(1000)
      .then((data) => {
        cars = data;
        toolbar.setFilters(filters);
        refreshList();
        if (selectedId !== null) {
          const exists = cars.find((c) => c.id === selectedId);
          if (exists) selectCar(selectedId);
          else loadDetail(selectedId);
        } else {
          renderCarDetail(detailContainer, null);
        }
      })
      .catch((err) => {
        setCarListError(listInner, err instanceof Error ? err.message : 'Erreur inconnue');
        renderCarDetail(detailContainer, null);
      });
  }

  function selectCar(id: number): void {
    selectedId = id;
    sessionStorage.setItem(SELECTED_ID_KEY, String(id));
    syncCarListSelection(listInner, selectedId);
    onSelectId?.(String(id));
    loadDetail(id);
  }

  function clearSelection(): void {
    selectedId = null;
    sessionStorage.removeItem(SELECTED_ID_KEY);
    syncCarListSelection(listInner, null);
    onSelectId?.(null);
    renderCarDetail(detailContainer, null);
  }

  function handleDeleteCar(car: Car): void {
    if (car.id == null) return;
    void openConfirmModal(modalRoot, {
      title: 'Supprimer la voiture',
      message: `Supprimer définitivement « ${car.name} » ?\n\nCette action est irréversible.`,
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      danger: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      void deleteCar(car.id!)
        .then(() => {
          cars = cars.filter((c) => c.id !== car.id);
          refreshList();
          if (selectedId === car.id) clearSelection();
        })
        .catch((err) => {
          window.alert(err instanceof Error ? err.message : 'Erreur lors de la suppression');
        });
    });
  }

  function loadDetail(id: number): void {
    setCarDetailLoading(detailContainer, true);
    fetchCarById(id)
      .then((car) => {
        renderCarDetail(detailContainer, car, {
          onSaved: (updated) => {
            const idx = cars.findIndex((c) => c.id === updated.id);
            if (idx >= 0) cars[idx] = updated;
            refreshList();
          },
          onDelete: handleDeleteCar,
        });
      })
      .catch((err) => {
        setCarDetailError(detailContainer, err instanceof Error ? err.message : 'Erreur inconnue');
      });
  }

  importInput.addEventListener('change', () => {
    const file = importInput.files?.[0];
    importInput.value = '';
    if (!file) return;
    void (async () => {
      try {
        const backup = await readCarBackupFromFile(file);
        const result = await importCarsBackup(backup);
        await loadList();
        const first = result.cars[0];
        if (first?.id != null) {
          selectCar(first.id);
        }
        window.alert(`${result.imported} voiture(s) importée(s) avec succès.`);
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Import impossible.');
      }
    })();
  });

  void loadList();

  return {
    dispose() {
      listHost.innerHTML = '';
      detailHost.innerHTML = '';
      actionsHost.innerHTML = '';
      importInput.remove();
    },
  };
}

/** Montage page autonome (tests / compat) : layout liste + détail + header. */
export function mountCarsApp(root: HTMLElement): void {
  const listHost = document.createElement('div');
  const detailHost = document.createElement('div');
  detailHost.className = 'circuits-main';

  const layout = document.createElement('div');
  layout.className = 'circuits-layout';
  layout.appendChild(listHost);
  layout.appendChild(detailHost);

  const header = document.createElement('header');
  header.className = 'circuits-header';
  header.style.display = 'flex';
  header.style.alignItems = 'center';
  header.style.justifyContent = 'space-between';
  header.style.flexWrap = 'wrap';
  header.style.gap = '1rem';
  const titleBlock = createGarageEntityTitleBlock('voitures');
  const headerActions = document.createElement('div');
  headerActions.className = 'circuits-header-actions';
  headerActions.style.display = 'flex';
  headerActions.style.gap = '0.5rem';
  headerActions.style.flexWrap = 'wrap';
  headerActions.style.alignItems = 'center';

  header.appendChild(titleBlock);
  header.appendChild(headerActions);

  root.innerHTML = '';
  root.appendChild(header);
  root.appendChild(layout);

  mountCarsPane({
    listHost,
    detailHost,
    actionsHost: headerActions,
    modalRoot: root,
  });
}
